/* eslint-disable import/prefer-default-export */
import { useEffect, useState } from "react";

// eslint-disable-next-line import/extensions
import {
  fetchRoiCalculation,
  extractGraphqlErrorMessage,
} from "../apicalls/roiFunctions";
// eslint-disable-next-line import/extensions
import { RoiCalculation } from "../../../graphql/types";

export type RoiCalculationFetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; calculation: RoiCalculation | null }
  | { status: "failed" };

interface UseRoiCalculationFetchArgs {
  customerId?: string;

  baselineReference?: string | null;
  interventionReference?: string;

  reloadToken?: number;
}

type TaggedState = RoiCalculationFetchState & { key: string };

export const useRoiCalculationFetch = ({
  customerId,
  baselineReference,
  interventionReference,
  reloadToken = 0,
}: UseRoiCalculationFetchArgs): RoiCalculationFetchState => {
  const key =
    customerId && baselineReference && interventionReference
      ? `${customerId}|${baselineReference}|${interventionReference}`
      : null;

  const [state, setState] = useState<TaggedState | null>(null);

  useEffect(() => {
    if (!key || !customerId || !baselineReference || !interventionReference) {
      return undefined;
    }

    let active = true;
    setState({ status: "loading", key });

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    (async () => {
      try {
        const calculation = await fetchRoiCalculation({
          customerId,
          baselineReference,
          interventionReference,
        });
        if (!active) {
          return;
        }

        setState({ status: "loaded", key, calculation });
      } catch (err) {
        if (!active) {
          return;
        }

        // eslint-disable-next-line no-console
        console.error(
          "getRoiCalculation failed:",
          extractGraphqlErrorMessage(err) ?? err,
        );
        setState({ status: "failed", key });
      }
    })();

    return () => {
      active = false;
    };
  }, [key, customerId, baselineReference, interventionReference, reloadToken]);

  if (!key) {
    return { status: "idle" };
  }

  if (!state || state.key !== key) {
    return { status: "loading" };
  }

  return state;
};
